/**
 * TheatreDetailPage — Standalone object-inspector for a Theatre.
 *
 * Routed at /theatres/:theatreId — the shared surface between
 * Workspace (where theatres are discovered) and Environment
 * (where theatres are composed and executed).
 *
 * Four regions:
 *   1. Header / Identity — state, construct, lineage
 *   2. Lifecycle / Readiness — state machine position + gate checks
 *   3. Operator Actions — context-aware controls for the current state
 *   4. Proof / Lineage / Provenance — commitment, certificate, timestamps
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  Circle,
  Clock3,
  Database,
  FileCheck2,
  GitCommitHorizontal,
  Layers,
  Loader2,
  Play,
  Radar,
  Rocket,
  SearchCheck,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  XCircle,
  Zap,
} from 'lucide-react';
import { useTheatreDetail } from '../hooks/useTheatreDetail';
import { DeployAgentModal } from '../components/agents/DeployAgentModal';
import type {
  ActivationCheck,
  TheatreCertificateResponse,
  TheatreResponse,
  TheatreState,
} from '../types/theatre';

// ── Formatting helpers ─────────────────────────────────────────────────

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '\u2014';
  return new Date(value).toLocaleString();
}

function formatCompact(value: string | null | undefined, leading = 10, trailing = 6): string {
  if (!value) return '\u2014';
  if (value.length <= leading + trailing + 1) return value;
  return `${value.slice(0, leading)}\u2026${value.slice(-trailing)}`;
}

function toTitleCase(value: string | null | undefined): string {
  if (!value) return '\u2014';
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

// ── State machine visual helpers ───────────────────────────────────────

const STATE_ORDER: TheatreState[] = [
  'DRAFT',
  'MATERIALIZED',
  'COMMITTED',
  'ACTIVE',
  'SETTLING',
  'RESOLVED',
];

function stateTone(state: TheatreResponse['state']) {
  switch (state) {
    case 'DRAFT':
      return 'border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] text-[var(--e-text-muted)]';
    case 'MATERIALIZED':
      return 'border-[var(--e-amber-200)] bg-[var(--e-amber-50)] text-[var(--e-amber-700)]';
    case 'COMMITTED':
      return 'border-[var(--e-purple-200)] bg-[var(--e-purple-50)] text-[var(--e-purple-700)]';
    case 'ACTIVE':
      return 'border-[color:oklch(0.545_0.170_152_/_0.18)] bg-[var(--e-green-50)] text-[var(--e-green-600)]';
    case 'SETTLING':
      return 'border-[color:oklch(0.708_0.136_62_/_0.20)] bg-[color:oklch(0.708_0.136_62_/_0.10)] text-[var(--e-orange-600)]';
    case 'RESOLVED':
      return 'border-[color:oklch(0.525_0.155_260_/_0.18)] bg-[color:oklch(0.525_0.155_260_/_0.08)] text-[var(--e-purple-700)]';
    case 'ARCHIVED':
      return 'border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] text-[var(--e-text-disabled)]';
    default:
      return 'border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] text-[var(--e-text-muted)]';
  }
}

function paradoxTone(level: string | null | undefined) {
  if (!level) {
    return {
      label: 'Healthy',
      classes:
        'border-[color:oklch(0.545_0.170_152_/_0.18)] bg-[var(--e-green-50)] text-[var(--e-green-600)]',
    };
  }
  const upper = level.toUpperCase();
  if (upper.includes('CRITICAL') || upper.includes('HIGH')) {
    return {
      label: toTitleCase(level),
      classes:
        'border-[color:oklch(0.545_0.185_25_/_0.18)] bg-[var(--e-red-50)] text-[var(--e-red-600)]',
    };
  }
  if (upper.includes('MEDIUM') || upper.includes('ELEVATED')) {
    return {
      label: toTitleCase(level),
      classes:
        'border-[color:oklch(0.708_0.136_62_/_0.20)] bg-[color:oklch(0.708_0.136_62_/_0.10)] text-[var(--e-orange-600)]',
    };
  }
  return {
    label: toTitleCase(level),
    classes:
      'border-[color:oklch(0.545_0.170_152_/_0.18)] bg-[color:oklch(0.545_0.170_152_/_0.08)] text-[var(--e-green-600)]',
  };
}

function routingTone(certificate: TheatreCertificateResponse | null) {
  if (!certificate?.routing_hint) {
    return { label: 'Pending', classes: 'text-[var(--e-text-muted)]', dot: 'bg-[var(--e-text-disabled)]' };
  }
  switch (certificate.routing_hint) {
    case 'ALLOWED':
      return { label: 'Allowed', classes: 'text-[var(--e-green-600)]', dot: 'bg-[var(--e-green-600)]' };
    case 'REVIEW_REQUIRED':
      return { label: 'Review Required', classes: 'text-[var(--e-orange-600)]', dot: 'bg-[var(--e-orange-600)]' };
    case 'BLOCKED':
      return { label: 'Blocked', classes: 'text-[var(--e-red-600)]', dot: 'bg-[var(--e-red-600)]' };
    default:
      return { label: certificate.routing_hint, classes: 'text-[var(--e-text-muted)]', dot: 'bg-[var(--e-text-disabled)]' };
  }
}

// ── Reusable subcomponents ─────────────────────────────────────────────

function SectionCard({
  title,
  eyebrow,
  children,
  aside,
}: {
  title: string;
  eyebrow?: string;
  children: React.ReactNode;
  aside?: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] shadow-[var(--e-shadow-xs)]">
      <div className="flex items-center justify-between gap-4 border-b border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-5 py-3">
        <div>
          {eyebrow ? (
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
              {eyebrow}
            </div>
          ) : null}
          <h2 className="text-[13px] font-semibold text-[var(--e-text-primary)]">{title}</h2>
        </div>
        {aside}
      </div>
      <div className="px-5 py-5">{children}</div>
    </section>
  );
}

function MetricTile({
  label,
  value,
  helper,
  tone,
}: {
  label: string;
  value: string;
  helper?: string;
  tone?: 'danger' | 'warning' | 'success';
}) {
  return (
    <div className="rounded-lg border border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-4 py-3">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
        {label}
      </div>
      <div
        className={clsx(
          'font-mono text-[18px] font-bold tabular-nums',
          tone === 'danger'
            ? 'text-[var(--e-red-600)]'
            : tone === 'warning'
              ? 'text-[var(--e-orange-600)]'
              : tone === 'success'
                ? 'text-[var(--e-green-600)]'
                : 'text-[var(--e-text-primary)]',
        )}
      >
        {value}
      </div>
      {helper ? <div className="mt-1 text-[11px] text-[var(--e-text-muted)]">{helper}</div> : null}
    </div>
  );
}

function ActionButton({
  label,
  icon,
  onClick,
  disabled,
  pending,
  tone = 'primary',
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  pending?: boolean;
  tone?: 'primary' | 'secondary';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || pending}
      className={clsx(
        'inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border px-4 text-[13px] font-semibold transition',
        tone === 'primary'
          ? 'border-[var(--e-purple-500)] bg-[var(--e-purple-500)] text-white hover:bg-[var(--e-purple-400)]'
          : 'border-[var(--e-border-primary)] bg-[var(--e-bg-card)] text-[var(--e-text-secondary)] hover:bg-[var(--e-bg-hover)] hover:text-[var(--e-text-primary)]',
        'disabled:cursor-not-allowed disabled:border-[var(--e-border-secondary)] disabled:bg-[var(--e-bg-sunken)] disabled:text-[var(--e-text-disabled)]',
      )}
    >
      {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      {label}
    </button>
  );
}

function ReadinessCheckRow({ check }: { check: ActivationCheck }) {
  return (
    <div className="flex items-start gap-2.5 py-2 first:pt-0 last:pb-0">
      {check.passed ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--e-green-600)]" />
      ) : (
        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--e-red-600)]" />
      )}
      <div className="min-w-0">
        <div className="text-[12px] font-medium text-[var(--e-text-primary)]">
          {toTitleCase(check.check)}
        </div>
        <div className="text-[11px] text-[var(--e-text-muted)]">{check.detail}</div>
      </div>
    </div>
  );
}

/** Horizontal state machine visualizer. */
function StateMachineBar({ current }: { current: TheatreState }) {
  const currentIdx = STATE_ORDER.indexOf(current);

  return (
    <div className="flex items-center gap-0">
      {STATE_ORDER.map((s, i) => {
        const isActive = s === current;
        const isPast = i < currentIdx;
        const isFuture = i > currentIdx;

        return (
          <div key={s} className="flex items-center">
            {i > 0 && (
              <div
                className={clsx(
                  'h-px w-5',
                  isPast ? 'bg-[var(--e-green-600)]' : 'bg-[var(--e-border-secondary)]',
                )}
              />
            )}
            <div
              className={clsx(
                'flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.04em]',
                isActive && stateTone(s),
                isPast &&
                  'border-[color:oklch(0.545_0.170_152_/_0.18)] bg-[var(--e-green-50)] text-[var(--e-green-600)]',
                isFuture &&
                  'border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] text-[var(--e-text-disabled)]',
              )}
            >
              {isPast ? (
                <CheckCircle2 className="h-2.5 w-2.5" />
              ) : isActive ? (
                <Circle className="h-2.5 w-2.5 fill-current" />
              ) : null}
              {s}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-5 p-6">
      <div className="h-4 w-40 animate-pulse rounded bg-[var(--e-bg-sunken)]" />
      <div className="h-12 w-96 max-w-full animate-pulse rounded bg-[var(--e-bg-sunken)]" />
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-5">
          <div className="h-64 animate-pulse rounded-lg border border-[var(--e-border-primary)] bg-[var(--e-bg-card)]" />
          <div className="h-64 animate-pulse rounded-lg border border-[var(--e-border-primary)] bg-[var(--e-bg-card)]" />
        </div>
        <div className="h-[420px] animate-pulse rounded-lg border border-[var(--e-border-primary)] bg-[var(--e-bg-card)]" />
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────

export function TheatreDetailPage() {
  const { theatreId } = useParams<{ theatreId: string }>();
  const navigate = useNavigate();
  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const {
    theatre,
    commitment,
    certificate,
    activationReadiness,
    runtimeReadiness,
    datasetBindingReadiness,
    isLoading,
    isCommitmentLoading,
    isCertificateLoading,
    error,
    commit,
    isCommitting,
    run,
    isRunning,
    settle,
    isSettling,
    activate,
    isActivating,
    bindDatasets,
    isBindingDatasets,
    startRuntime,
    isStartingRuntime,
  } = useTheatreDetail(theatreId ?? null, { includeInvestigationContext: true });

  const paradox = paradoxTone(theatre?.paradox_risk_level);
  const routing = routingTone(certificate);
  const isForge = theatre?.inquiry_class === 'FORGE_EVALUATION';

  const wrapAction = (fn: () => Promise<unknown>, fallbackMsg: string) => async () => {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : fallbackMsg);
    }
  };

  if (isLoading) return <DetailSkeleton />;

  if (error || !theatre) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-5xl rounded-lg border border-[color:oklch(0.545_0.185_25_/_0.18)] bg-[var(--e-bg-card)] px-8 py-14 text-center shadow-[var(--e-shadow-xs)]">
          <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-[var(--e-red-600)]" />
          <h1 className="mb-2 text-[22px] font-semibold text-[var(--e-text-primary)]">Unable to load theatre</h1>
          <p className="mb-6 text-[14px] text-[var(--e-text-secondary)]">
            {error instanceof Error ? error.message : 'This theatre could not be loaded.'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/workspace')}
            className="inline-flex h-10 items-center gap-2 rounded-md border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] px-4 text-[13px] font-semibold text-[var(--e-text-secondary)] transition hover:bg-[var(--e-bg-hover)] hover:text-[var(--e-text-primary)]"
          >
            <ArrowLeft className="h-4 w-4" />
            Workspace
          </button>
        </div>
      </div>
    );
  }

  const progressValue = `${Math.round(theatre.progress * 100)}%`;
  const hasDatasetHashes =
    commitment?.dataset_hashes && Object.keys(commitment.dataset_hashes).length > 0;

  return (
    <div className="animate-fade-in p-6">
      <div className="mx-auto max-w-7xl">
        {/* ── Region 1: Header / Identity ───────────────────────────────── */}

        <div className="mb-2 flex items-center gap-2 text-[13px] text-[var(--e-text-muted)]">
          <button
            type="button"
            onClick={() => navigate('/workspace')}
            className="inline-flex items-center gap-1.5 transition hover:text-[var(--e-purple-700)]"
          >
            <ArrowLeft className="h-4 w-4" />
            Workspace
          </button>
          <span>/</span>
          <span className="text-[var(--e-text-secondary)]">Theatres</span>
          <span>/</span>
          <span className="font-medium text-[var(--e-text-primary)]">{theatre.construct_id}</span>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span
            className={clsx(
              'inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.05em]',
              stateTone(theatre.state),
            )}
          >
            {theatre.state}
          </span>
          <span
            className={clsx(
              'inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.05em]',
              paradox.classes,
            )}
          >
            {paradox.label}
          </span>
          {theatre.inquiry_class ? (
            <span className="inline-flex items-center rounded-full border border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--e-text-muted)]">
              {theatre.inquiry_class}
            </span>
          ) : null}
        </div>

        <h1 className="max-w-4xl text-[28px] font-semibold leading-[1.15] tracking-[-0.02em] text-[var(--e-text-primary)]">
          {theatre.construct_id}
        </h1>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-[var(--e-text-secondary)]">
          <span className="font-mono text-[var(--e-text-muted)]">{formatCompact(theatre.id, 12, 6)}</span>
          {theatre.stop_condition && <span>Stop: {toTitleCase(theatre.stop_condition)}</span>}
          <span>Created {formatDateTime(theatre.created_at)}</span>
        </div>

        {/* Environment navigation */}
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to={`/theatres/${theatre.id}/build`}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] px-3 text-[12px] font-medium text-[var(--e-text-secondary)] no-underline transition hover:bg-[var(--e-bg-hover)] hover:text-[var(--e-text-primary)]"
          >
            <Layers className="h-3.5 w-3.5" />
            Environment Build
            <ArrowRight className="h-3 w-3" />
          </Link>
          <Link
            to={`/theatres/${theatre.id}/monitor`}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] px-3 text-[12px] font-medium text-[var(--e-text-secondary)] no-underline transition hover:bg-[var(--e-bg-hover)] hover:text-[var(--e-text-primary)]"
          >
            <Radar className="h-3.5 w-3.5" />
            Environment Monitor
            <ArrowRight className="h-3 w-3" />
          </Link>
          <Link
            to={`/investigation/create?theatre_id=${encodeURIComponent(theatre.id)}&construct_id=${encodeURIComponent(theatre.construct_id)}&source_surface=theatre_detail`}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] px-3 text-[12px] font-medium text-[var(--e-text-secondary)] no-underline transition hover:bg-[var(--e-bg-hover)] hover:text-[var(--e-text-primary)]"
          >
            <SearchCheck className="h-3.5 w-3.5" />
            Investigation
          </Link>
        </div>

        {/* State machine bar */}
        <div className="mt-5 mb-6 overflow-x-auto">
          <StateMachineBar current={theatre.state} />
        </div>

        {/* ── Main grid: content + sidebar ──────────────────────────────── */}

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
          {/* ── LEFT: Lifecycle + Overview ────────────────────────────────── */}
          <div className="space-y-5">
            {/* Overview metrics */}
            <SectionCard
              title="Theatre Overview"
              eyebrow="Construct"
              aside={
                <div className="text-[12px] font-medium text-[var(--e-text-muted)]">
                  Progress{' '}
                  <span className="font-mono tabular-nums text-[var(--e-text-primary)]">
                    {progressValue}
                  </span>
                </div>
              }
            >
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <MetricTile label="State" value={theatre.state} />
                <MetricTile
                  label="Episodes"
                  value={`${theatre.total_episodes}`}
                  helper="Total planned episodes"
                />
                <MetricTile
                  label="Failures"
                  value={`${theatre.failure_count}`}
                  helper={theatre.error ?? 'No errors'}
                  tone={theatre.failure_count > 0 ? 'warning' : undefined}
                />
                <MetricTile
                  label="Certificate"
                  value={theatre.certificate_id ? 'Issued' : 'Pending'}
                  helper={
                    theatre.certificate_id
                      ? formatCompact(theatre.certificate_id, 10, 6)
                      : 'Awaiting resolution'
                  }
                  tone={theatre.certificate_id ? 'success' : undefined}
                />
              </div>
            </SectionCard>

            {/* ── Region 2: Lifecycle / Readiness ──────────────────────────── */}

            {/* Activation readiness (MATERIALIZED only) */}
            {theatre.state === 'MATERIALIZED' && activationReadiness && (
              <SectionCard title="Activation Readiness" eyebrow="MATERIALIZED \u2192 COMMITTED">
                <div className="mb-3 rounded-lg border border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-4 py-3">
                  <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                    Blocking Checks
                  </div>
                  <div className="divide-y divide-[var(--e-border-secondary)]">
                    {activationReadiness.blocking_checks.map((c) => (
                      <ReadinessCheckRow key={c.check} check={c} />
                    ))}
                  </div>
                </div>
                {activationReadiness.advisory_checks.length > 0 && (
                  <div className="rounded-lg border border-dashed border-[var(--e-border-primary)] bg-[var(--e-bg-sunken)] px-4 py-3">
                    <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                      Advisory (not blocking)
                    </div>
                    <div className="divide-y divide-[var(--e-border-secondary)]">
                      {activationReadiness.advisory_checks.map((c) => (
                        <ReadinessCheckRow key={c.check} check={c} />
                      ))}
                    </div>
                  </div>
                )}
              </SectionCard>
            )}

            {/* Dataset binding readiness (COMMITTED + FORGE only) */}
            {theatre.state === 'COMMITTED' && isForge && datasetBindingReadiness && (
              <SectionCard title="Dataset Binding" eyebrow="FORGE Source References">
                <div className="mb-3 rounded-lg border border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-4 py-3">
                  <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                    Binding Checks
                  </div>
                  <div className="divide-y divide-[var(--e-border-secondary)]">
                    {datasetBindingReadiness.checks.map((c) => (
                      <ReadinessCheckRow key={c.check} check={c} />
                    ))}
                  </div>
                </div>
                {datasetBindingReadiness.manifest_preview && (
                  <div className="rounded-lg border border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-4 py-3">
                    <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                      Source Manifest Preview
                    </div>
                    <div className="space-y-1.5">
                      {Object.entries(datasetBindingReadiness.manifest_preview)
                        .filter(([, v]) => v != null && v !== '')
                        .map(([k, v]) => (
                          <div key={k} className="flex items-center justify-between text-[12px]">
                            <span className="text-[var(--e-text-secondary)]">{toTitleCase(k)}</span>
                            <span className="max-w-[200px] truncate font-mono text-[var(--e-text-primary)]">
                              {String(v)}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </SectionCard>
            )}

            {/* Runtime readiness (COMMITTED only) */}
            {theatre.state === 'COMMITTED' && runtimeReadiness && (
              <SectionCard title="Runtime Readiness" eyebrow="COMMITTED \u2192 ACTIVE">
                <div className="mb-3 rounded-lg border border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-4 py-3">
                  <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                    Blocking Checks
                  </div>
                  <div className="divide-y divide-[var(--e-border-secondary)]">
                    {runtimeReadiness.blocking_checks.map((c) => (
                      <ReadinessCheckRow key={c.check} check={c} />
                    ))}
                  </div>
                </div>
                {runtimeReadiness.advisory_checks.length > 0 && (
                  <div className="rounded-lg border border-dashed border-[var(--e-border-primary)] bg-[var(--e-bg-sunken)] px-4 py-3">
                    <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                      Advisory
                    </div>
                    <div className="divide-y divide-[var(--e-border-secondary)]">
                      {runtimeReadiness.advisory_checks.map((c) => (
                        <ReadinessCheckRow key={c.check} check={c} />
                      ))}
                    </div>
                  </div>
                )}
                {runtimeReadiness.deferred_for_runtime.length > 0 && (
                  <div className="mt-3 text-[11px] text-[var(--e-text-muted)]">
                    Deferred: {runtimeReadiness.deferred_for_runtime.join(', ')}
                  </div>
                )}
              </SectionCard>
            )}

            {/* ── Region 4: Proof / Lineage ──────────────────────────────── */}

            <SectionCard title="Commitment Receipt" eyebrow="Pinned Runtime">
              {theatre.commitment_hash ? (
                isCommitmentLoading ? (
                  <div className="flex items-center gap-2 text-[13px] text-[var(--e-text-muted)]">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading commitment receipt\u2026
                  </div>
                ) : commitment ? (
                  <div className="space-y-4">
                    <div className="grid gap-3 md:grid-cols-2">
                      <MetricTile
                        label="Commitment Hash"
                        value={formatCompact(commitment.commitment_hash, 12, 8)}
                      />
                      <MetricTile
                        label="Committed At"
                        value={formatDateTime(commitment.committed_at)}
                      />
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="rounded-lg border border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-4 py-4">
                        <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                          Version Pins
                        </div>
                        <div className="space-y-2">
                          {Object.keys(commitment.version_pins).length > 0 ? (
                            Object.entries(commitment.version_pins).map(([key, value]) => (
                              <div
                                key={key}
                                className="flex items-center justify-between gap-3 text-[12px]"
                              >
                                <span className="text-[var(--e-text-secondary)]">
                                  {toTitleCase(key)}
                                </span>
                                <span className="font-mono text-[var(--e-text-primary)]">
                                  {value}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="text-[12px] text-[var(--e-text-muted)]">
                              No version pins
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="rounded-lg border border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-4 py-4">
                        <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                          Dataset Hashes
                        </div>
                        <div className="space-y-2">
                          {hasDatasetHashes ? (
                            Object.entries(commitment.dataset_hashes).map(([key, value]) => (
                              <div
                                key={key}
                                className="flex items-center justify-between gap-3 text-[12px]"
                              >
                                <span className="text-[var(--e-text-secondary)]">
                                  {toTitleCase(key)}
                                </span>
                                <span className="font-mono text-[var(--e-text-primary)]">
                                  {formatCompact(value, 10, 6)}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="text-[12px] text-[var(--e-text-muted)]">
                              {isForge
                                ? 'Bind FORGE datasets to populate'
                                : 'No dataset hashes'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-[13px] leading-6 text-[var(--e-text-secondary)]">
                    Committed, but the receipt is not available from the current query.
                  </p>
                )
              ) : (
                <div className="rounded-lg border border-dashed border-[var(--e-border-primary)] bg-[var(--e-bg-sunken)] px-4 py-4 text-[13px] leading-6 text-[var(--e-text-secondary)]">
                  {theatre.state === 'MATERIALIZED' ? (
                    <>
                      This theatre is{' '}
                      <span className="font-semibold text-[var(--e-text-primary)]">
                        MATERIALIZED
                      </span>{' '}
                      from a FORGE proposal. Activate it to generate the commitment hash.
                    </>
                  ) : (
                    <>
                      This theatre is in{' '}
                      <span className="font-semibold text-[var(--e-text-primary)]">
                        {theatre.state}
                      </span>
                      . Commit the theatre to generate the locked commitment receipt.
                    </>
                  )}
                </div>
              )}
            </SectionCard>

            {/* Investigation context */}
            {theatre.investigation_context?.investigation_summary ? (
              <SectionCard title="Investigation Lineage" eyebrow="Provenance">
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-4 text-[12px]">
                    <span className="text-[var(--e-text-secondary)]">Investigation</span>
                    <span className="font-mono text-[var(--e-text-primary)]">
                      {formatCompact(
                        theatre.investigation_context.investigation_summary.investigation_id,
                        10,
                        6,
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4 text-[12px]">
                    <span className="text-[var(--e-text-secondary)]">Construct</span>
                    <span className="font-mono text-[var(--e-text-primary)]">
                      {theatre.investigation_context.investigation_summary.construct_id}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4 text-[12px]">
                    <span className="text-[var(--e-text-secondary)]">Status</span>
                    <span className="font-mono text-[var(--e-text-primary)]">
                      {theatre.investigation_context.investigation_summary.status}
                    </span>
                  </div>
                </div>
              </SectionCard>
            ) : (
              <SectionCard title="Investigation Context" eyebrow="Provenance">
                <div className="rounded-lg border border-dashed border-[var(--e-border-primary)] bg-[var(--e-bg-sunken)] px-4 py-4">
                  <div className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-[var(--e-text-primary)]">
                    <Sparkles className="h-4 w-4 text-[var(--e-purple-700)]" />
                    No linked investigation
                  </div>
                  <p className="text-[13px] leading-6 text-[var(--e-text-secondary)]">
                    This theatre has no linked investigation yet. Create one from the
                    investigation link above to establish evidence lineage.
                  </p>
                </div>
              </SectionCard>
            )}
          </div>

          {/* ── RIGHT: Actions + Certificate + Timestamps (sidebar) ────── */}
          <div className="space-y-5 lg:sticky lg:top-[104px]">
            {/* ── Region 3: Operator Actions ──────────────────────────────── */}
            <SectionCard title="Theatre Controls" eyebrow="Operator Actions">
              <div className="space-y-3">
                {/* MATERIALIZED → activate */}
                {theatre.state === 'MATERIALIZED' && (
                  <ActionButton
                    label="Activate Theatre"
                    icon={<Zap className="h-4 w-4" />}
                    onClick={wrapAction(activate, 'Failed to activate theatre')}
                    disabled={activationReadiness ? !activationReadiness.activatable : true}
                    pending={isActivating}
                    tone="primary"
                  />
                )}

                {/* COMMITTED + FORGE → bind datasets */}
                {theatre.state === 'COMMITTED' && isForge && !hasDatasetHashes && (
                  <ActionButton
                    label="Bind FORGE Datasets"
                    icon={<Database className="h-4 w-4" />}
                    onClick={wrapAction(bindDatasets, 'Failed to bind datasets')}
                    disabled={
                      datasetBindingReadiness ? !datasetBindingReadiness.bindable : true
                    }
                    pending={isBindingDatasets}
                    tone="primary"
                  />
                )}

                {/* COMMITTED → start runtime */}
                {theatre.state === 'COMMITTED' && (
                  <ActionButton
                    label="Start Runtime"
                    icon={<Play className="h-4 w-4" />}
                    onClick={wrapAction(startRuntime, 'Failed to start runtime')}
                    disabled={runtimeReadiness ? !runtimeReadiness.runnable : true}
                    pending={isStartingRuntime}
                    tone={
                      runtimeReadiness?.runnable ? 'primary' : 'secondary'
                    }
                  />
                )}

                {/* DRAFT → commit */}
                {theatre.state === 'DRAFT' && (
                  <ActionButton
                    label="Commit Theatre"
                    icon={<GitCommitHorizontal className="h-4 w-4" />}
                    onClick={wrapAction(commit, 'Failed to commit')}
                    pending={isCommitting}
                    tone="primary"
                  />
                )}

                {/* COMMITTED (non-FORGE) → run (legacy path) */}
                {theatre.state === 'COMMITTED' && !isForge && (
                  <ActionButton
                    label="Start Run"
                    icon={<Play className="h-4 w-4" />}
                    onClick={wrapAction(run, 'Failed to start run')}
                    pending={isRunning}
                    tone="secondary"
                  />
                )}

                {/* ACTIVE → settle */}
                {theatre.state === 'ACTIVE' && (
                  <ActionButton
                    label="Settle Theatre"
                    icon={<BadgeCheck className="h-4 w-4" />}
                    onClick={wrapAction(settle, 'Failed to settle')}
                    pending={isSettling}
                    tone="primary"
                  />
                )}

                {/* Deploy agent (non-terminal states) */}
                {theatre.state !== 'RESOLVED' && theatre.state !== 'ARCHIVED' && (
                  <ActionButton
                    label="Deploy Agent"
                    icon={<Rocket className="h-4 w-4" />}
                    onClick={() => setDeployModalOpen(true)}
                    tone="secondary"
                  />
                )}
              </div>

              {actionError && (
                <div className="mt-4 flex items-start gap-2 rounded-md border border-[color:oklch(0.545_0.185_25_/_0.18)] bg-[var(--e-red-50)] px-3 py-3 text-[12px] leading-5 text-[var(--e-red-600)]">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{actionError}</span>
                </div>
              )}
            </SectionCard>

            {/* Certificate & routing */}
            <SectionCard title="Certificate & Routing" eyebrow="Resolution Output">
              {theatre.state === 'RESOLVED' || theatre.certificate_id ? (
                isCertificateLoading ? (
                  <div className="flex items-center gap-2 text-[13px] text-[var(--e-text-muted)]">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading certificate\u2026
                  </div>
                ) : certificate ? (
                  <div className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <MetricTile
                        label="Composite Score"
                        value={certificate.composite_score.toFixed(3)}
                      />
                      <MetricTile
                        label="Verification Tier"
                        value={certificate.verification_tier}
                      />
                    </div>
                    <div className="rounded-lg border border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-4 py-4">
                      <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                        Routing
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-[13px] text-[var(--e-text-secondary)]">
                            Routing hint
                          </span>
                          <span
                            className={clsx(
                              'inline-flex items-center gap-2 text-[12px] font-semibold',
                              routing.classes,
                            )}
                          >
                            <span className={clsx('h-2.5 w-2.5 rounded-full', routing.dot)} />
                            {routing.label}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-[13px] text-[var(--e-text-secondary)]">
                            Coherence gate
                          </span>
                          <span className="font-mono text-[12px] text-[var(--e-text-primary)]">
                            {certificate.coherence_gate_status ?? '\u2014'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-[13px] text-[var(--e-text-secondary)]">
                            Replay count
                          </span>
                          <span className="font-mono text-[12px] text-[var(--e-text-primary)]">
                            {certificate.replay_count}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Link
                        to="/certificates"
                        className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-md border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] text-[13px] font-semibold text-[var(--e-text-secondary)] no-underline transition hover:bg-[var(--e-bg-hover)] hover:text-[var(--e-text-primary)]"
                      >
                        <FileCheck2 className="h-4 w-4" />
                        Ledger
                      </Link>
                      <Link
                        to={`/verify?certificate=${encodeURIComponent(certificate.id)}`}
                        className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-md border border-[var(--e-purple-500)] bg-[var(--e-purple-500)] text-[13px] font-semibold text-white no-underline transition hover:bg-[var(--e-purple-400)]"
                      >
                        <ShieldCheck className="h-4 w-4" />
                        Verify
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-[var(--e-border-primary)] bg-[var(--e-bg-sunken)] px-4 py-4 text-[13px] leading-6 text-[var(--e-text-secondary)]">
                    Resolved, but the certificate is not currently available.
                  </div>
                )
              ) : (
                <div className="rounded-lg border border-dashed border-[var(--e-border-primary)] bg-[var(--e-bg-sunken)] px-4 py-4 text-[13px] leading-6 text-[var(--e-text-secondary)]">
                  Certificates appear after settlement.
                </div>
              )}
            </SectionCard>

            {/* Activity trace */}
            <SectionCard title="Activity Trace" eyebrow="Timestamps">
              <div className="space-y-3">
                {[
                  { label: 'Created', value: theatre.created_at, icon: Clock3 },
                  { label: 'Committed', value: theatre.committed_at, icon: GitCommitHorizontal },
                  { label: 'Resolved', value: theatre.resolved_at, icon: BadgeCheck },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-3 rounded-lg border border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-4 py-3"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] text-[var(--e-purple-700)]">
                      <item.icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--e-text-muted)]">
                        {item.label}
                      </div>
                      <div className="font-mono text-[12px] text-[var(--e-text-primary)]">
                        {formatDateTime(item.value)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>
        </div>
      </div>

      <DeployAgentModal
        open={deployModalOpen}
        onClose={() => setDeployModalOpen(false)}
        preselectedTheatreId={theatreId}
      />
    </div>
  );
}
